import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * ProtectedRoute
 * Wraps a component and checks whether the current user has the required
 * module permission.  If not, shows an "Access Denied" notice and redirects
 * to the home/campaign listing page after 3 seconds.
 *
 * Usage (in frontRoutes.js):
 *   { path: '/listing_users', permission: 'users', component: UserListing }
 *
 * If `permission` is null / undefined the route is always accessible.
 */
const ProtectedRoute = ({ component: Component, permission }) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);

  const userPermissions = window.localStorage.getItem("user-permissions") || "";
  const permissionArr = userPermissions ? userPermissions.split(",") : [];
  const hasAccess = !permission || permissionArr.includes(permission);

  useEffect(() => {
    if (hasAccess) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/campaign_listing");
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [hasAccess, navigate]);

  if (hasAccess) {
    return <Component />;
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#e74c3c"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 style={styles.title}>Access Denied</h2>
        <p style={styles.message}>
          You don't have permission to access this page.
          <br />
          Please contact your administrator if you think this is a mistake.
        </p>
        <p style={styles.countdown}>
          Redirecting to home in <strong>{countdown}</strong> second{countdown !== 1 ? "s" : ""}…
        </p>
        <button
          style={styles.btn}
          onClick={() => navigate("/campaign_listing")}
        >
          Go to Home Now
        </button>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "70vh",
    padding: "24px",
  },
  card: {
    background: "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: "12px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    padding: "48px 40px",
    maxWidth: "440px",
    width: "100%",
    textAlign: "center",
  },
  iconWrap: {
    marginBottom: "20px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a1a2e",
    margin: "0 0 12px",
  },
  message: {
    fontSize: "15px",
    color: "#666",
    lineHeight: "1.6",
    margin: "0 0 20px",
  },
  countdown: {
    fontSize: "14px",
    color: "#999",
    margin: "0 0 24px",
  },
  btn: {
    background: "#3b6cf4",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 28px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

export default ProtectedRoute;
